/**
 * The footer binds this area too.
 *
 * `[x]` §21.4 requires the About Us and Contact Us entry points to be
 * reachable from every public AND gated surface — the row names both sides
 * explicitly — so the footer is not the public site's alone. The account menu
 * carries the same two destinations, so a reader on a short page and a reader
 * in the menu both reach them.
 *
 * The design system names no footer component and no footer class, so nothing
 * is invented: the element is a semantic `<footer>`, every child is a
 * documented class or a plain anchor, and the area-level layout rides a data
 * attribute rather than a new class name.
 *
 * Both informational pages are served on the public host, so both links leave —
 * and البحث does NOT, because search is a surface of this app. Each entry
 * declares which it is; the element follows from the declaration rather than
 * from where the entry happens to sit in the row.
 *
 * **The entry set is a prop with the authenticated set as its default**, because
 * the pre-session shell carries a shorter one: the two informational pages the
 * `[x]` row names, without the two catalogue destinations. That is not a second
 * footer — it is this footer with the slots a reader outside a session cannot
 * use left out, exactly as the auth shell drops the avatar and the account menu.
 */
import { Link } from 'react-router-dom';

import { formatFigure } from '@shared/format';

import type { FooterEntry } from './nav';
import { chromeCopy } from './copy';
import { BrandLockup } from './icons';
import { CROSS_HOST_LINK, footerEntries } from './nav';

const ENTRY_CLASS =
  'text-sm text-ink-muted no-underline transition-colors duration-fast hover:text-accent-text hover:underline';

export interface FooterProps {
  /** Defaults to the authenticated set. The auth shell passes the informational pair. */
  entries?: readonly FooterEntry[];
}

export function Footer({ entries = footerEntries }: FooterProps = {}) {
  return (
    <footer data-site-footer="" className="mt-section-gap border-t border-border bg-bg-sunken">
      {/*
        ONE row, exactly as the public site draws it. Mark, links, proposition
        and copyright sit on a single wrapping line, so the footer costs one
        line of height on a wide viewport instead of two stacked blocks. It
        wraps rather than shrinks below that width: the proposition is a
        sentence and a truncated sentence states nothing.

        No block-end padding for the bottom bar here, unlike the public site's
        footer. `AppShell` already carries `pb-[calc(4.5rem+safe-area)]` on the
        shell wrapper, so the fixed bar's height is reserved once, above this
        element, rather than twice.
      */}
      <div className="container flex flex-wrap items-center gap-x-4 gap-y-2 py-6">
        {/*
          The full lockup, not the seal beside the name set in the display
          family. Those are two different drawings and only one of them is the
          brand: design-system §8 ships the word as outlined paths precisely
          so it never depends on a font having loaded and no `letter-spacing`
          can break an Arabic join. This footer drew live text until now,
          which is the one thing §8 rules out.

          No `hidden sm:block` here. The footer row is the brand and two
          links, so it has the width the top bar does not and shows the
          lockup at every size.
        */}
        <BrandLockup
          className="flex shrink-0 items-center gap-2 text-ink"
          lockupClassName="h-[2rem] w-[8.288rem] shrink-0"
          label={chromeCopy.brand}
        />

        <nav aria-label={chromeCopy.footerLabel} className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {entries.map((entry) =>
            // The public host's documents — من نحن and اتصل بنا — open BESIDE
            // the app rather than replacing it. البحث and المقالات are routes
            // here, so they navigate in place like any other link in the app.
            entry.external === false ? (
              <Link key={entry.id} to={entry.href} className={ENTRY_CLASS}>
                {entry.label}
              </Link>
            ) : (
              <a key={entry.id} href={entry.href} {...CROSS_HOST_LINK} className={ENTRY_CLASS}>
                {entry.label}
              </a>
            ),
          )}
        </nav>

        {/*
          `ms-auto` pushes the proposition and the copyright to the inline end
          of the row while the row still fits on one line; once it wraps the
          margin has nothing to take up and the two simply fall to the next
          line.
        */}
        <p className="mb-0 ms-auto text-xs leading-relaxed text-ink-muted">{chromeCopy.footerNote}</p>

        {/*
          The year is a FIGURE, not a quantity — `formatFigure` is the
          ungrouped Arabic-Indic formatter, so «٢٠٢٦» never renders as
          «٢٬٠٢٦» — and it is `<bdi>`-isolated because a numeral opens a
          line that continues in Arabic.

          This area is a client-rendered SPA with no server pass, so reading
          the clock here cannot desynchronise a hydration the way it would on
          the public site, which takes the year as a prop for that reason.
        */}
        <p className="mb-0 text-xs text-ink-muted">
          <bdi>{formatFigure(new Date().getFullYear())}</bdi> {chromeCopy.brand} — {chromeCopy.copyright}
        </p>
      </div>
    </footer>
  );
}
